import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Clock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import logoImage from "@assets/favicon new no background cc6633.png";

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const status = params.get("status");

  useEffect(() => {
    if (status === "success" || status === "already-verified") {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    }
  }, [status]);

  const handleResend = async () => {
    setResending(true);
    try {
      await apiRequest("POST", "/api/auth/resend-verification");
      toast({
        title: "Email Sent",
        description: "A new verification email has been sent. Please check your inbox.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to resend verification email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center space-y-4">
            <Link href="/" className="flex justify-center" data-testid="link-verify-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-20" />
            </Link>
            <div className="flex justify-center">
              <CheckCircle2 className="w-16 h-16 text-green-500" data-testid="icon-verified" />
            </div>
            <div>
              <CardTitle className="text-2xl" data-testid="text-verify-title">
                You're verified — welcome to Fill The Trucks
              </CardTitle>
              <CardDescription className="text-base mt-2">
                Your email address has been confirmed. Click below to log in and get started.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full"
              size="lg"
              onClick={() => setLocation(user ? "/dashboard" : "/login")}
              data-testid="button-continue"
            >
              {user ? "Go to Dashboard" : "Log In"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "already-verified") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center space-y-4">
            <Link href="/" className="flex justify-center" data-testid="link-verify-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-20" />
            </Link>
            <div className="flex justify-center">
              <CheckCircle2 className="w-16 h-16 text-green-500" data-testid="icon-already-verified" />
            </div>
            <div>
              <CardTitle className="text-2xl" data-testid="text-verify-title">Already Verified</CardTitle>
              <CardDescription className="text-base mt-2">
                Your email address has already been verified.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full"
              size="lg"
              onClick={() => setLocation(user ? "/dashboard" : "/login")}
              data-testid="button-continue"
            >
              {user ? "Go to Dashboard" : "Log In"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Link href="/" className="flex justify-center" data-testid="link-verify-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-20" />
            </Link>
            <div className="flex justify-center mb-4">
              <Clock className="w-16 h-16 text-yellow-500" data-testid="icon-expired" />
            </div>
            <CardTitle className="text-2xl" data-testid="text-verify-title">Link Expired</CardTitle>
            <CardDescription className="text-base mt-2">
              This verification link has expired. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {user && !user.emailVerified && (
              <Button
                className="w-full"
                size="lg"
                onClick={handleResend}
                disabled={resending}
                data-testid="button-resend"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            )}
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={() => setLocation(user ? "/dashboard" : "/login")}
              data-testid="button-continue"
            >
              {user ? "Go to Dashboard" : "Log In"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "invalid" || status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Link href="/" className="flex justify-center" data-testid="link-verify-logo">
              <img src={logoImage} alt="Fill The Trucks" className="h-20" />
            </Link>
            <div className="flex justify-center mb-4">
              <XCircle className="w-16 h-16 text-destructive" data-testid="icon-invalid" />
            </div>
            <CardTitle className="text-2xl" data-testid="text-verify-title">
              {status === "invalid" ? "Invalid Link" : "Verification Error"}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {status === "invalid"
                ? "This verification link is invalid or has already been used."
                : "Something went wrong during verification. Please try again."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {user && !user.emailVerified && (
              <Button
                className="w-full"
                size="lg"
                onClick={handleResend}
                disabled={resending}
                data-testid="button-resend"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            )}
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={() => setLocation(user ? "/dashboard" : "/login")}
              data-testid="button-continue"
            >
              {user ? "Go to Dashboard" : "Log In"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="w-16 h-16 text-primary" data-testid="icon-check-email" />
          </div>
          <CardTitle className="text-2xl" data-testid="text-verify-title">Check Your Email</CardTitle>
          <CardDescription className="text-base mt-2">
            We sent a verification link to {user?.email ? <strong>{user.email}</strong> : "your email address"}.
            Please click the link to verify your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Didn't receive the email? Check your spam folder, or request a new one below.
          </p>
          {user && (
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={handleResend}
              disabled={resending}
              data-testid="button-resend"
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
          )}
          <Button
            className="w-full"
            size="lg"
            variant="outline"
            onClick={() => setLocation(user ? "/dashboard" : "/login")}
            data-testid="button-continue"
          >
            {user ? "Continue to Dashboard" : "Go to Login"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
